import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { skillApi } from '@/services/skillKnowledgeApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Eye, Edit, Trash2, Brain, Power, PowerOff } from 'lucide-react';
import { LoadingState, EmptyState, Pagination } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Skill } from '@/types/skillKnowledge';

export const SkillListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterDomain, setFilterDomain] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'skill');
  const canEdit = hasPermission('edit', 'skill');
  const canDelete = hasPermission('delete', 'skill');
  const canActivate = hasPermission('activate', 'skill');
  const canDeactivate = hasPermission('deactivate', 'skill');

  // Build fetch function with filters
  const fetchSkillsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      const listParams: Record<string, unknown> = {
        ...params,
        sortBy: 'name',
        sortDirection: 'asc',
      };
      if (filterCategory !== 'all') listParams.category = filterCategory;
      if (filterDomain !== 'all') listParams.domain = filterDomain;
      if (filterStatus !== 'all') listParams.status = filterStatus;
      if (filterActive !== 'all') listParams.isActive = filterActive === 'true';
      // Note: searchQuery is not currently supported by the API, but we keep it for future use
      return skillApi.list(listParams);
    };
  }, [filterCategory, filterDomain, filterStatus, filterActive]);

  // Use paginated list hook
  const {
    data: skills,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<Skill>({
    fetchFn: fetchSkillsFn,
    entityName: 'skills',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await skillApi.delete(id);
    },
    entityName: 'skill',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: async (id: string) => {
      await skillApi.activate(id);
    },
    entityName: 'skill',
    operation: 'update',
    successMessage: 'Skill activated successfully',
    onSuccess: () => {
      refetch();
    },
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      await skillApi.deactivate(id);
    },
    entityName: 'skill',
    operation: 'update',
    successMessage: 'Skill deactivated successfully',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this skill?')) return;
    await deleteMutation.mutate(id);
  };

  const handleActivate = async (id: string) => {
    await activateMutation.mutate(id);
  };

  const handleDeactivate = async (id: string) => {
    await deactivateMutation.mutate(id);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Skill Management</h1>
          <p className="text-muted-foreground mt-2">Manage all skills</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/skills/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Skill
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="TECHNICAL">Technical</SelectItem>
                <SelectItem value="SOFT_SKILLS">Soft Skills</SelectItem>
                <SelectItem value="LEADERSHIP">Leadership</SelectItem>
                <SelectItem value="MANAGEMENT">Management</SelectItem>
                <SelectItem value="CREATIVE">Creative</SelectItem>
                <SelectItem value="ANALYTICAL">Analytical</SelectItem>
                <SelectItem value="COMMUNICATION">Communication</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterDomain} onValueChange={setFilterDomain}>
              <SelectTrigger>
                <SelectValue placeholder="All Domains" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Domains</SelectItem>
                <SelectItem value="PROGRAMMING">Programming</SelectItem>
                <SelectItem value="DATA_SCIENCE">Data Science</SelectItem>
                <SelectItem value="SOFTWARE_ENGINEERING">Software Engineering</SelectItem>
                <SelectItem value="AI_MACHINE_LEARNING">AI/ML</SelectItem>
                <SelectItem value="CLOUD_INFRASTRUCTURE">Cloud Infrastructure</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger>
                <SelectValue placeholder="Active Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Skills Table */}
      <Card>
        <CardHeader>
          <CardTitle>Skills ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all skills</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading skills..." />
          ) : (error || skills.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load skills. Please try again or create a new one." : "Get started by creating your first skill."}
              icon={Brain}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/skills/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Levels</TableHead>
                    <TableHead>Difficulty</TableHead>
                    <TableHead>Popularity</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {skills.map((skill) => (
                    <TableRow key={skill.id}>
                      <TableCell className="font-medium">{skill.name}</TableCell>
                      <TableCell>{skill.category}</TableCell>
                      <TableCell>{skill.domain}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            skill.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : skill.status === 'DRAFT'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}
                        >
                          {skill.status}
                        </span>
                      </TableCell>
                      <TableCell>{skill.availableSkillLevels?.join(', ') || 'N/A'}</TableCell>
                      <TableCell>{skill.difficultyRating || 'N/A'}</TableCell>
                      <TableCell>{skill.popularityScore?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell>
                        {formatDate(skill.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/skills/${skill.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/skills/${skill.id}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canActivate && !skill.isActive && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => skill.id && handleActivate(skill.id)}
                              title="Activate"
                              disabled={activateMutation.isLoading}
                            >
                              <Power className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {canDeactivate && skill.isActive && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => skill.id && handleDeactivate(skill.id)}
                              title="Deactivate"
                              disabled={deactivateMutation.isLoading}
                            >
                              <PowerOff className="h-4 w-4 text-orange-600" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => skill.id && handleDelete(skill.id)}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalElements={pagination.totalElements}
                size={pagination.size}
                onPageChange={setPage}
                onSizeChange={setSize}
                isLoading={isLoading}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SkillListPage;
